// Chakra imports
import {
	Button,
	ButtonGroup,
	Flex,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Text,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import { IconDotsVertical } from '@tabler/icons';
import { useQueryClient } from '@tanstack/react-query';
import { ButtonComponent } from 'components/buttons/ButtonComponent';
import ExportGenericCSVButton from 'components/buttons/exportButtons/ExportGenericCSVButton';
import Card from 'components/card/Card';
import CheckTable from 'components/dataTables/CheckTable';
import CheckTableModal from 'components/modal/CheckTableModal';
import { FileUploadModal } from 'components/modal/fileUpload/FileUploadModal';
import { ModalDeleteHierarchy } from 'components/modal/ModalDeleteHierarchy';
import { TooltipStandard } from 'components/tooltip/TooltipStandard';
import { useGlobalContext } from 'contexts/GlobalContext';
import { SidebarHelpContext } from 'contexts/SidebarHelpContext';
import { ISidebarHelp } from 'models/sidebarHelp/sidebarHelp.model';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { deleteHierarchyByFilters } from 'services/api/requests/commercial';
import { useDownloadTemplateHierarchy } from 'services/queryClient/wrapperHooks/uploads/useDownloadTemplateHierarchy';
import { useUploadCommercialHierarchy } from 'services/queryClient/wrapperHooks/uploads/useUploadCommercialHierarchy';
import { URL_CT360, URL_FALE_CONOSCO } from 'utils/consts/global';
import { formatStringoToDateAndTime } from 'utils/functions/dateUtils';

import { buildCheckTableModalProps } from './functions/util';
import { columnsDataHierarquiaComercial } from './variables/columnsDataHierarquiaComercial';
import { hierarchyMessages } from './variables/hierarchyMessages';

interface IHierarchy {
	id: number;
	code: string;
	nome_mercado: string;
	nome_canal_venda: string;
	nome_forca_venda: string;
	nome_zona_venda: string;
	nome_membro: string;
	cycleId: string | number;
	data_registro: string;
}

export default function CadastroMercados() {
	const { isOpen, onOpen, onClose } = useDisclosure();
	const {
		isOpen: isOpenDeleteModal,
		onOpen: onOpenDeleteModal,
		onClose: onCloseDeleteModal,
	} = useDisclosure();

	const history = useHistory();
	const toast = useToast();
	const queryClient = useQueryClient();

	const { pathCompanyName } = useGlobalContext();
	const { setters } = useContext(SidebarHelpContext);
	const { setDataSidebar } = setters;

	const {
		isOpen: isOpenModalList,
		onOpen: onOpenModalList,
		onClose: onCloseModalList,
	} = useDisclosure();

	const [selectedModuleProps, setSelectedModuleProps] = useState<any>({});
	const [hierarchyToDelete, setHierarchyToDelete] = useState<{
		ids: number[];
		todos: boolean;
		filters?: any[];
	} | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	const tableColumns = useMemo(() => columnsDataHierarquiaComercial, []);
	const [hasBaseData, setHasBaseData] = useState<boolean>(false);

	const helpMarket: ISidebarHelp = useMemo(
		() => ({
			title: hierarchyMessages.HELP.TITLE,
			firstParagraph: hierarchyMessages.HELP.FIRST_PARAGRAPH,
			secondParagraph: hierarchyMessages.HELP.SECOND_PARAGRAPH,
			firstLink: URL_CT360,
			secondLink: URL_FALE_CONOSCO,
		}),
		[]
	);

	useEffect(() => {
		setDataSidebar(helpMarket);
	}, [helpMarket, setDataSidebar]);

	const goToCadastroHierarquia = useCallback(() => {
		history.push(`/${pathCompanyName}/cadastros/hierarquias/criar`);
	}, [history, pathCompanyName]);

	const handleDeleteClick = useCallback(
		(ids: number[], todos: boolean, filters?: any[]) => {
			setHierarchyToDelete({ ids, todos, filters });
			onOpenDeleteModal();
		},
		[onOpenDeleteModal]
	);

	const handleConfirmDeletion = useCallback(
		async (action: 'realocar' | 'excluir', typesToDelete?: string[]) => {
			if (!hierarchyToDelete) return;

			setIsDeleting(true);
			try {
				const payload = {
					...hierarchyToDelete,
					action: action,
					typesToDelete: typesToDelete,
				};

				await deleteHierarchyByFilters(payload);

				if (action === 'realocar') {
					toast({
						title: hierarchyMessages.TOASTS.REALLOCATE_SUCCESS,
						status: 'success',
						duration: 5000,
						isClosable: true,
						position: 'bottom-left',
					});
					toast({
						title: hierarchyMessages.TOASTS.DELETE_SUCCESS,
						status: 'success',
						duration: 5000,
						isClosable: true,
						position: 'bottom-left',
					});
				} else {
					toast({
						title: hierarchyMessages.TOASTS.DELETE_STARTED,
						status: 'warning',
						duration: 7000,
						isClosable: true,
						position: 'bottom-left',
					});
					setTimeout(() => {
						toast({
							title: hierarchyMessages.TOASTS
								.RECORDS_DELETED_SUCCESS,
							status: 'success',
							duration: 5000,
							isClosable: true,
							position: 'bottom-left',
						});
					}, 2000);
				}

				queryClient.invalidateQueries(['hierarquiaComercial-list']);
				onCloseDeleteModal();
				setHierarchyToDelete(null);
			} catch (error: any) {
				toast({
					title: hierarchyMessages.TOASTS.INTERNAL_ERROR,
					status: 'error',
					duration: 5000,
					isClosable: true,
					position: 'bottom-left',
				});
			} finally {
				setIsDeleting(false);
			}
		},
		[hierarchyToDelete, toast, queryClient, onCloseDeleteModal]
	);

	const dataDelete = {
		provider: 'hierarchy',
		toastSuccessTitle: hierarchyMessages.DELETE.SUCCESS_TITLE,
		toastSuccessDescription: hierarchyMessages.DELETE.SUCCESS_DESCRIPTION,
		toastErrorTitle: hierarchyMessages.DELETE.ERROR_TITLE,
		title: hierarchyMessages.DELETE.CONFIRM_TITLE,
		text: hierarchyMessages.DELETE.CONFIRM_TEXT,
		invalidate: 'hierarquiaComercial-list',
		onDeleteClick: handleDeleteClick,
	};

	const deleteMessage = useMemo(
		() => hierarchyMessages.DELETE.BULK_MESSAGE,
		[]
	);

	const dataDeleteAll = useMemo(
		() => ({
			provider: 'hierarchy',
			toastSuccessTitle: hierarchyMessages.DELETE.BULK_SUCCESS_TITLE,
			toastSuccessDescription:
				hierarchyMessages.DELETE.BULK_SUCCESS_DESCRIPTION,
			toastErrorTitle: hierarchyMessages.DELETE.BULK_ERROR_TITLE,
			invalidate: 'hierarquiaComercial-list',
			onDeleteClick: handleDeleteClick,
		}),
		[handleDeleteClick]
	);

	const renderRow = useCallback((hierarchy: IHierarchy) => {
		return {
			id: hierarchy.id,
			code: hierarchy.code,
			nomeMercado: hierarchy.nome_mercado,
			canalVenda: hierarchy.nome_canal_venda,
			forcaVenda: hierarchy.nome_forca_venda,
			zonaVenda: hierarchy.nome_zona_venda,
			membro: hierarchy.nome_membro,
			dataRegistro: formatStringoToDateAndTime(hierarchy?.data_registro),
		};
	}, []);

	const handleOnOpenModalList = useCallback(
		module => {
			setSelectedModuleProps(buildCheckTableModalProps(module));

			setTimeout(() => onOpenModalList(), 300);
		},
		[onOpenModalList]
	);

	const renderMenuListOptions = useCallback(() => {
		return (
			<Menu>
				<MenuButton
					as={Button}
					rightIcon={
						<Flex mr='6px'>
							<IconDotsVertical />
						</Flex>
					}
					size='large'
					fontWeight='100'
					px='6px'
					py='2px'
					border='1px solid'
					borderRadius='100px'
					borderColor='easyBrandMenu.200'
					fontSize='16px'
					color='easyBLUE.300'
					_hover={{ bg: '#FAFCFE' }}
					_focus={{ bg: 'white' }}
					_active={{ bg: '#E2E8F0', boxShadow: 'none' }}
				/>
				<MenuList>
					<MenuItem onClick={() => handleOnOpenModalList('mercado')}>
						<Text color='easyBLUE.300'>
							{hierarchyMessages.MENU.VIEW_MARKETS}
						</Text>
					</MenuItem>
					<MenuItem
						onClick={() => handleOnOpenModalList('canalVenda')}
					>
						<Text color='easyBLUE.300'>
							{hierarchyMessages.MENU.VIEW_SALES_CHANNELS}
						</Text>
					</MenuItem>
					<MenuItem
						onClick={() => handleOnOpenModalList('forcaVenda')}
					>
						<Text color='easyBLUE.300'>
							{hierarchyMessages.MENU.VIEW_SALES_FORCES}
						</Text>
					</MenuItem>
					<MenuItem
						onClick={() => handleOnOpenModalList('zonaVenda')}
					>
						<Text color='easyBLUE.300'>
							{hierarchyMessages.MENU.VIEW_SALES_ZONES}
						</Text>
					</MenuItem>
				</MenuList>
			</Menu>
		);
	}, [handleOnOpenModalList]);

	const renderButtons = useCallback((): JSX.Element => {
		return (
			<Flex
				flexDirection={'row'}
				justifyContent={'flex-end'}
				gap={2}
				mt={'20px'}
				me={'20px'}
				mb={'40px'}
			>
				<ButtonGroup spacing={'20px'}>
					<ButtonComponent
						data-test='button-importar-hierarquia'
						type={'ghost'}
						title={hierarchyMessages.BUTTONS.IMPORT_HIERARCHIES}
						onClick={onOpen}
					/>
					<FileUploadModal
						isOpen={isOpen}
						onClose={onClose}
						headerText={hierarchyMessages.BUTTONS.IMPORT_DATA_TITLE}
						module={'hierarquiaComercial'}
						useDownloadTemplate={useDownloadTemplateHierarchy}
						stringInvalidQuery='hierarquia-comercial'
						useUpload={useUploadCommercialHierarchy}
						showLineRestricts={true}
						columnsData={tableColumns}
						hasBaseData={hasBaseData}
					/>
					<ButtonComponent
						data-test='button-cadastrar-hierarquia'
						type={'primary'}
						title={hierarchyMessages.BUTTONS.CREATE_HIERARCHIES}
						onClick={goToCadastroHierarquia}
					/>
					<TooltipStandard
						label={hierarchyMessages.BUTTONS.EXPORT_TOOLTIP}
					>
						<ExportGenericCSVButton
							nameExport='hierarquiaComercial'
							module={'hierarquiaComercial'}
						/>
					</TooltipStandard>
					{renderMenuListOptions()}
				</ButtonGroup>
			</Flex>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		goToCadastroHierarquia,
		renderMenuListOptions,
		isOpen,
		onClose,
		tableColumns,
		hasBaseData,
	]);

	const renderTable = (): JSX.Element => {
		return (
			<CheckTable
				dataTest='button-filtrar-hierarquia'
				module='hierarquiaComercial'
				linkEditForm={`/${pathCompanyName}/cadastros/hierarquia/editar`}
				columnsData={tableColumns}
				dataDelete={dataDelete}
				filterModule='hierarquiaComercial'
				renderRow={renderRow}
				deleteMessage={deleteMessage}
				bulkDeleteProps={dataDeleteAll}
				showGenericSearchInput={false}
				setHasBaseData={setHasBaseData}
				tableTest='tabela-hierarquia'
			/>
		);
	};

	return (
		<Flex
			direction='column'
			pt={{ sm: '125px', lg: '75px' }}
		>
			<Card px='0px'>
				{renderButtons()}
				{renderTable()}
			</Card>
			<CheckTableModal
				filterModule={selectedModuleProps?.filterModule}
				columnsData={selectedModuleProps?.columnsData}
				renderRow={selectedModuleProps?.renderRow}
				isOpen={isOpenModalList}
				onClose={onCloseModalList}
				modalTitle={selectedModuleProps?.modalTitle}
				simpleDelete={selectedModuleProps?.simpleDelete}
				deleteMany={selectedModuleProps?.deleteMany}
				deleteAll={selectedModuleProps?.deleteAll}
				queriesToInvalidadeOnRemove={['hierarquiaComercial-list']}
				queriesToInvalidadeOnEdit={['hierarquiaComercial-list']}
			/>
			<ModalDeleteHierarchy
				isOpen={isOpenDeleteModal}
				onClose={onCloseDeleteModal}
				onConfirm={handleConfirmDeletion}
				hierarquiaId={
					hierarchyToDelete?.ids && hierarchyToDelete.ids.length > 0
						? hierarchyToDelete.ids[0]
						: null
				}
				isLoading={isDeleting}
			/>
		</Flex>
	);
}
